'use client'
import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva } from 'class-variance-authority'
import { clsx } from 'clsx'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../dropdown-menu'
import {
  DropdownMenuContentProps,
  DropdownMenuItemProps,
} from '@radix-ui/react-dropdown-menu'
import { Button } from '../button'
import { ScrollArea } from '../scroll-area'

export const Breadcrumb = ({ children }: { children: React.ReactNode }) => {
  const viewportRef = React.useRef<HTMLDivElement>(null)
  const isScrollAnchoredRef = React.useRef(true)
  const [shouldRenderShadow, setShouldRenderShadow] = React.useState(false)

  React.useEffect(() => {
    const scrollable = viewportRef.current
    if (!scrollable) return

    const handleMutation = () => {
      if (!isScrollAnchoredRef.current) return
      // Batch the scroll call
      requestAnimationFrame(() => {
        scrollable.scrollTo({
          left: scrollable.scrollWidth,
          behavior: 'instant',
        })
      })
    }

    const observer = new MutationObserver(handleMutation)

    observer.observe(scrollable, {
      childList: true,
      subtree: true,
      characterData: true,
    })

    // Kick off first scroll
    handleMutation()

    return () => {
      observer.disconnect()
    }
  }, [])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollLeft, clientWidth, scrollWidth } = e.currentTarget
    const isAnchored = scrollLeft + clientWidth >= scrollWidth - 8
    if (isScrollAnchoredRef.current !== isAnchored) {
      isScrollAnchoredRef.current = isAnchored
      setShouldRenderShadow(!isAnchored)
    }
  }

  return (
    <ScrollArea
      viewportRef={viewportRef}
      className="max-w-[85%]"
      orientation="horizontal"
      variant="subtle"
      viewportOnScroll={handleScroll}
    >
      <nav className="w-max flex items-center justify-start p-1 pl-4 gap-2 min-w-0 flex-1 h-[calc(var(--editor-breadcrumb-height)-1px)]">
        {children}
      </nav>
      <div
        className="absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-dark-shade to-transparent pointer-events-none"
        style={{
          opacity: shouldRenderShadow ? 1 : 0,
          transition: 'opacity 0.15s ease-in-out',
        }}
      />
    </ScrollArea>
  )
}

const $breadcrumbItem = cva('cursor-default truncate', {
  variants: {
    active: {
      true: '!text-dark-bright',
      false: 'hover:text-dark-muted',
    },
  },
})

type BreadcrumbItemProps = {
  onClick?: () => void
  active: boolean
  className?: string
  children: React.ReactNode
  asChild?: boolean
}
export const BreadcrumbItem = ({
  onClick,
  active,
  className,
  children,
  asChild,
  ...props
}: BreadcrumbItemProps) => {
  const Comp = asChild ? Slot : Button
  return (
    <div
      className={clsx(
        'flex items-center [&>span]:last-of-type:hidden gap-2 cursor-default'
      )}
    >
      <Comp
        onClick={onClick}
        unstyled
        focusStyled
        className={$breadcrumbItem({ active, className })}
        {...props}
      >
        {children}
      </Comp>
      <BreadcrumbSeparator />
    </div>
  )
}
export const BreadcrumbEllipsis = () => {
  return (
    <div className="flex items-center gap-2 cursor-default">
      <span>...</span>
      <BreadcrumbSeparator />
    </div>
  )
}

export const BreadcrumbEllipsisDropdown = ({
  children,
  ...props
}: {
  children: React.ReactNode
} & DropdownMenuContentProps) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <BreadcrumbEllipsis />
      </DropdownMenuTrigger>
      <DropdownMenuContent asChild {...props}>
        <ul>{children}</ul>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const BreadcrumbEllipsisDropdownItem = ({
  onClick,
  children,
  className,
  ...props
}: {
  onClick: () => void
  children: React.ReactNode
} & DropdownMenuItemProps) => {
  return (
    <DropdownMenuItem
      asChild
      onClick={onClick}
      className={className}
      {...props}
    >
      <li>
        <p className="max-w-[40ch] w-full truncate">{children}</p>
      </li>
    </DropdownMenuItem>
  )
}

export const BreadcrumbSeparator = () => {
  return (
    <span className="text-dark-muted select-none pointer-events-none">/</span>
  )
}
